import { equalJson } from '@dte/otw-utils-core/src/objects/equalJson';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useJsonEqualState<T>(initialValue?: T): [T | undefined, (value: T | undefined) => void] {
	// Track changes in the input
	const [value, setValue] = useState<T | undefined>(initialValue);
	const previousValue = useRef<T | undefined>(initialValue);

	// Ensure that the output remains stable
	const setValueIfDifferent = useCallback((newValue: T | undefined) => {
		// Value hasn't changed
		if (equalJson(previousValue.current, newValue)) {
			return;
		}

		// Update the value
		previousValue.current = newValue;
		setValue(newValue);
	}, []);

	return [value, setValueIfDifferent];
}

// Use a value that only changes if the json equality changes
export function useJsonEqualValue<T>(currentValue: T | undefined): T | undefined {
	const [value, setValue] = useJsonEqualState(currentValue);

	useEffect(() => {
		setValue(currentValue);
	}, [currentValue, setValue]);

	return value;
}
